import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { authAPI, platformAPI } from '../api';
import MaestraLogo from '../components/MaestraLogo';
import RateLimitNotice from '../components/RateLimitNotice';
import LegalFooterLinks from '../components/LegalFooterLinks';
import PlatformFooter from '../components/PlatformFooter';

const LoginPage = () => {
  const navigate = useNavigate();
  document.title = 'Maestra - Sign in';

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [rateLimitMessage, setRateLimitMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [ptx, setPtx] = useState(null);

  useEffect(() => {
    // Check if setup is complete
    const checkSetup = async () => {
      try {
        const { data } = await authAPI.checkSetup();
        if (!data.setup_complete) {
          navigate('/setup');
        }
      } catch (err) {
        console.error('Error checking setup:', err);
      } finally {
        setCheckingSetup(false);
      }
    };

    // Check if already logged in
    const token = Cookies.get('kahla_token');
    if (token) {
      navigate('/dashboard');
      return;
    }

    checkSetup();

    // Fetch platform texts (public endpoint)
    platformAPI.getTexts().then(res => setPtx(res.data)).catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRateLimitMessage('');
    setLoading(true);

    try {
      const { data } = await authAPI.login(identifier, password);
      console.log('Login response:', data);
      
      // Set cookie with proper attributes for cross-site compatibility
      Cookies.set('kahla_token', data.access_token, { 
        expires: 1,
        path: '/',
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:'
      });
      
      // Store role
      if (data.role) {
        Cookies.set('kahla_role', data.role, {
          expires: 1,
          path: '/',
          sameSite: 'Lax',
          secure: window.location.protocol === 'https:'
        });
      }
      
      // Verify cookie was set
      const storedToken = Cookies.get('kahla_token');
      console.log('Token stored in cookie:', storedToken ? 'Yes' : 'No');
      
      if (!storedToken) {
        setError('Failed to store authentication token. Please check your browser settings.');
        return;
      }
      
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Invalid credentials';
      if (status === 429) {
        // Calm, italic notice rendered above the form. We deliberately do NOT
        // populate `error` so the destructive red pill stays hidden.
        setRateLimitMessage(detail);
      } else if (detail === 'archived_frozen') {
        setError('Your workspace has been archived. Please contact the administrator to restore access.');
      } else if (status === 401) {
        // Calmer copy than "Invalid credentials" — most users will read this.
        setError('We couldn\u2019t find an account matching those details. Check your email and password and try again.');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="login-page">
        <div className="login-container" style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '40px auto' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="login-container">
        <div style={{ marginBottom: '32px' }}><MaestraLogo size="sm" /></div>
        <h1 className="login-title" data-testid="login-title">{ptx?.login_title || 'Welcome Back'}</h1>
        {ptx?.login_description && (
          <p data-testid="login-description" style={{
            fontSize: '13px',
            color: 'var(--kahla-text-light)',
            textAlign: 'center',
            marginBottom: '16px',
            letterSpacing: '0.02em',
          }}>{ptx.login_description}</p>
        )}
        
        {error && <div className="login-error">{error}</div>}
        <RateLimitNotice message={rateLimitMessage} testId="login-rate-limit-notice" />
        
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="login-identifier">Email or username</label>
            <input
              id="login-identifier"
              type="text"
              autoComplete="username"
              inputMode="email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="you@example.com"
              data-testid="login-identifier-input"
              required
            />
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: '13px',
              lineHeight: 1.55,
              color: 'var(--kahla-text-light)',
              margin: '6px 0 0',
            }}>
              Use the email connected to your workspace.
            </p>
          </div>

          <div className="login-field">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              data-testid="login-password-input"
              required
            />
          </div>
          
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading}
            style={{ width: '100%', marginTop: '12px' }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            data-testid="forgot-password-link"
            onClick={() => navigate('/forgot-password')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              letterSpacing: '0.04em',
              color: 'var(--kahla-text-light)',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              padding: 0,
            }}
          >
            Forgot your password?
          </button>
        </div>
        
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/')}
            style={{ fontSize: '12px', padding: '10px 20px' }}
          >
            ← Back to Home
          </button>
        </div>
        </div>
      </div>

      <footer data-testid="login-footer" style={{
        width: '100vw',
        padding: '24px',
        textAlign: 'center',
        borderTop: '1px solid var(--kahla-border)',
        marginTop: 'auto',
      }}>
        <PlatformFooter />
        <LegalFooterLinks marginTop={12} />
      </footer>
    </div>
  );
};

export default LoginPage;
